import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Dumbbell, History, LayoutDashboard, Library, Moon, Sun, LineChart, LogOut, Target, ListChecks, Trophy, Menu, X, Star, MessageSquare, Bell, Settings as SettingsIcon, Users, Trash2, ShoppingBag, ShieldAlert, Download, Share, Mail, Sparkles, FileText } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import Dashboard from './pages/Dashboard';
import HistoryPage from './pages/History';
import ExerciseLibrary from './pages/ExerciseLibrary';
import BodyTracker from './pages/BodyTracker';
import ProfileSelection from './pages/ProfileSelection';
import MyProfile from './pages/MyProfile';
import Goals from './pages/Goals';
import Routines from './pages/Routines';
import Social from './pages/Social';
import Leaderboard from './pages/Leaderboard';
import Badges from './pages/Badges';
import Forum from './pages/Forum';
import PublicProfile from './pages/PublicProfile';
import Settings from './pages/Settings';
import MascotTest from './pages/MascotTest';
import MascotShowcase from './pages/MascotShowcase';
import Changelog from './pages/Changelog';
import Shop from './pages/Shop';
import AdminDashboard from './pages/AdminDashboard';
import NotFound from './pages/NotFound';
import TestBoss from './pages/TestBoss';
import TestBossV2 from './pages/TestBossV2';
import TestBossV3 from './pages/TestBossV3';
import Messages from './pages/Messages';
import GlobalFeedback from './components/GlobalFeedback';
import { FloatingFeedbackButton } from './components/FeedbackButton';
import { db } from './db';
import { getLevelInfo } from './utils/xpUtils';
import { useAuth } from './context/AuthContext';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthAction from './pages/AuthAction';
import Contact from './pages/Contact';
import LegalMentions from './pages/LegalMentions';
import CGU from './pages/CGU';
import PolicyPage from './pages/PolicyPage';
import AccountSettings from './components/AccountSettings';
import Loader from './components/Loader';
import { shopItems } from './data/shopItems';
import { toast } from './utils/feedback';
import UserAvatar from './components/UserAvatar';
import VerificationScreen from './components/VerificationScreen';
import OnboardingScreen from './components/OnboardingScreen';
import { doc, getDoc } from 'firebase/firestore';
import { firestoreDb } from './firebase';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const Navigation = ({ currentProfile }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const requestsCount = currentProfile?.receivedRequests?.length || 0;
  
  const mainNavItems = [
    { path: '/', label: 'Dashboard', icon: <LayoutDashboard size={26} /> },
    { path: '/routines', label: 'Programmes', icon: <ListChecks size={26} /> },
    { path: '/goals', label: 'Objectifs', icon: <Target size={26} /> },
    { path: '/leaderboard', label: 'Classement', icon: <Trophy size={26} /> },
  ];

  const menuItems = [
    { path: '/history', label: 'Historique', icon: <History size={26} /> },
    { path: '/body', label: 'Évolution Physique', icon: <LineChart size={26} /> },
    { path: '/library', label: 'Exercices', icon: <Library size={26} /> },
    { path: '/social', label: 'Social', icon: <Users size={26} /> },
    { path: '/messages', label: 'Messagerie', icon: <Mail size={26} /> },
    { path: '/badges', label: 'Mes Succès', icon: <Star size={26} /> },
    { path: '/forum', label: 'Forum', icon: <MessageSquare size={26} /> },
    { path: '/shop', label: 'Boutique', icon: <ShoppingBag size={26} /> },
  ];

  if (currentProfile?.id === 'VX3fSmgFvr20XLYuzZv8') {
    menuItems.push({ path: '/admin', label: 'Admin', icon: <ShieldAlert size={26} /> });
  }

  return (
    <div className="main-nav-container" style={{ position: 'relative', marginBottom: '2rem' }}>
      <style>{`
        .menu-dropdown-item:hover {
          background-color: rgba(168, 159, 207, 0.1) !important;
        }
        nav::-webkit-scrollbar { display: none; }
        nav { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <nav className="main-nav" style={{
        display: 'flex',
        alignItems: 'stretch',
        gap: '0.5rem',
        padding: '0.5rem',
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--border-radius)',
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow)',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        {mainNavItems.map(item => (
          <Link 
            key={item.path} 
            to={item.path}
            onClick={() => setIsMenuOpen(false)}
            className="nav-item"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: 'var(--border-radius-sm)',
              backgroundColor: location.pathname === item.path && !isMenuOpen ? 'var(--primary-color)' : 'transparent',
              color: location.pathname === item.path && !isMenuOpen ? '#fff' : 'var(--text-primary)',
              fontWeight: location.pathname === item.path && !isMenuOpen ? '600' : '400',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              flex: '1 1 0px',
              minWidth: 0
            }}
          >
            <div className="nav-icon-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.icon}
            </div>
            <span className="nav-label">{item.label}</span>
          </Link>
        ))}
        
        {/* Menu Button */}
        <div 
          className="nav-item nav-menu-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: 'var(--border-radius-sm)',
            backgroundColor: isMenuOpen ? 'var(--primary-color)' : 'transparent',
            color: isMenuOpen ? '#fff' : 'var(--text-primary)',
            fontWeight: isMenuOpen ? '600' : '400',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: '1 1 0px',
            minWidth: 0
          }}
        >
          <div className="nav-icon-wrapper nav-menu-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
            {requestsCount > 0 && !isMenuOpen && (
              <span style={{ position: 'absolute', top: '-5px', right: '-8px', backgroundColor: 'var(--danger-color)', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', zIndex: 2 }}>
                {requestsCount}
              </span>
            )}
          </div>
          <span className="nav-label">Menu</span>
        </div>
      </nav>

      {/* Dropdown Menu Overlay */}
      {isMenuOpen && (
        <>
          <div 
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} 
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="main-nav-dropdown" style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: '0',
            backgroundColor: 'var(--bg-card)',
            zIndex: 999,
            padding: '0.5rem',
            borderRadius: 'var(--border-radius)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            minWidth: '200px',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            {menuItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className="menu-dropdown-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  padding: '1rem',
                  borderRadius: 'var(--border-radius-sm)',
                  color: location.pathname === item.path ? 'var(--primary-color)' : 'var(--text-primary)',
                  textDecoration: 'none',
                  backgroundColor: location.pathname === item.path ? 'rgba(168, 159, 207, 0.1)' : 'transparent',
                  fontWeight: location.pathname === item.path ? '600' : '400',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ position: 'relative' }}>
                  {item.icon}
                  {item.path === '/social' && requestsCount > 0 && (
                    <span style={{ position: 'absolute', top: '-5px', right: '-8px', backgroundColor: 'var(--danger-color)', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', zIndex: 2 }}>
                      {requestsCount}
                    </span>
                  )}
                </div>
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Header = ({ theme, toggleTheme, currentProfile, onLogout, deferredPrompt, handleInstallClick, isIosInstall, previewThemeId }) => {
  const { level, progress, xpInCurrentLevel, xpForNextLevel, totalXp, grade } = getLevelInfo(currentProfile?.xp || 0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const [selectedFeedbackReply, setSelectedFeedbackReply] = useState(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentProfile) {
      const unsubscribe = db.listenToNotifications(currentProfile.id, (notifs) => {
        setNotifications(notifs);
      });
      return () => unsubscribe();
    }
  }, [currentProfile]);

  const unreadCount = notifications.filter(n => !n.read && (!window.activeConvoId || n.convoId !== window.activeConvoId)).length;

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await db.markNotificationRead(notif.id);
    }
    
    setShowNotifs(false);
    
    if (notif.type === 'feedback_reply' && notif.fullMessage) {
      setSelectedFeedbackReply(notif.fullMessage);
      return;
    }

    if (notif.link) {
      navigate(notif.link);
    } else if (notif.postId) {
      navigate(`/forum#${notif.postId}`);
    }
  };

  const handleMarkAllRead = async () => {
    if (currentProfile) {
      await db.markAllNotificationsRead(currentProfile.id);
    }
  };

  const handleDeleteAllNotifs = async () => {
    if (currentProfile) {
      await db.deleteAllNotifications(currentProfile.id);
    }
  };

  const handleDeleteNotif = async (e, notifId) => {
    e.stopPropagation();
    await db.deleteNotification(notifId);
  };

  const equippedBorder = shopItems.find(i => i.id === currentProfile?.equipped?.border);
  const equippedTitle = shopItems.find(i => i.id === currentProfile?.equipped?.title);

  const renderLogo = () => {
    const activeThemeId = previewThemeId || currentProfile?.equipped?.theme;
    
    if (!activeThemeId) {
      // Default theme: Original colors
      return <img src="/logos/logo-officiel-square.png" className="header-logo-img" alt="Logo Masse-terclass" style={{ borderRadius: '8px' }} />;
    }
    
    // Custom themes
    let backgroundStyle = 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))';
    if (activeThemeId === 'theme_nature') {
      backgroundStyle = 'var(--primary-color)'; // Only solid green for Zen
    }

    return (
      <div className="header-logo-container" style={{ display: 'flex', position: 'relative' }}>
        <img src="/logos/logo-officiel-square.png" className="header-logo-img" alt="" style={{ visibility: 'hidden', borderRadius: '8px' }} />
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: backgroundStyle,
          WebkitMaskImage: 'url(/logos/logo-officiel-square.png)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskImage: 'url(/logos/logo-officiel-square.png)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center'
        }} title="Logo Masse-terclass" />
      </div>
    );
  };

  const headerStyle = currentProfile ? {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '2rem',
    backgroundColor: 'var(--bg-card)',
    padding: '1rem',
    borderRadius: 'var(--border-radius)',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow)'
  } : {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '2rem',
    padding: '0'
  };

  return (
    <header className="app-header" style={headerStyle}>
      <div className="app-header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'nowrap', gap: '0.5rem' }}>
        <h1 
          className="app-logo" 
          style={{ margin: 0, color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
          onClick={() => navigate('/')}
          title="Masse-terclass"
        >
          {renderLogo()} <span className="app-logo-text">Masse-terclass</span>
        </h1>
        
        <div className="header-actions">
          {currentProfile && (
            <>
              <div className="hidden-mobile" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginRight: '1rem' }}>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', backgroundColor: 'var(--bg-color)', padding: '0.4rem 0.8rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold' }} title="Pièces">
                    🪙 <span>{currentProfile.coins || 0}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold' }} title="Diamants">
                    💎 <span>{currentProfile.diamonds || 0}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: '0.85rem' }} title={`Niveau ${level} : ${xpInCurrentLevel} / ${xpForNextLevel} XP`}>
                  <span style={{ fontWeight: 'bold', color: 'var(--secondary-color)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal' }}>{grade} (Niv. {level})</span> 
                    <span style={{ color: 'var(--text-primary)' }}>{totalXp} XP</span>
                  </span>
                  <div style={{ width: '140px', height: '6px', backgroundColor: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden', marginTop: '4px', position: 'relative' }} title={`Niveau ${level} : ${xpInCurrentLevel} / ${xpForNextLevel} XP\nTotal cumulé : ${totalXp} XP`}>
                    <div style={{ width: `${progress}%`, height: '100%', backgroundColor: 'var(--secondary-color)', transition: 'width 0.3s ease' }}></div>
                  </div>
                </div>
              </div>

              <div 
                className="profile-pill"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--bg-color)', padding: '0.3rem 0.6rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', cursor: 'pointer' }}
                onClick={() => navigate('/profile')}
              >
                <UserAvatar profile={currentProfile} size={32} />
                <div className="profile-name-container" style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="profile-name" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {currentProfile.name}
                    {currentProfile.isBetaTester && <span title="Beta Testeur">🎖️</span>}
                  </span>
                </div>
                <button className="btn-icon logout-btn" onClick={(e) => { e.stopPropagation(); onLogout(); }} title="Changer de profil" style={{ padding: '0.3rem', marginLeft: '0.2rem' }}>
                  <LogOut size={18} />
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <button className="btn-icon" onClick={() => setShowNotifs(!showNotifs)}>
                  <Bell size={24} />
                  {unreadCount > 0 && (
                    <div style={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'var(--danger-color)', color: 'white', width: '18px', height: '18px', borderRadius: '50%', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                      {unreadCount}
                    </div>
                  )}
                </button>
                {showNotifs && (
                  <>
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} onClick={() => setShowNotifs(false)} />
                    <div className="notifications-dropdown">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Notifications</h3>
                        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
                          {unreadCount > 0 && (
                            <button 
                              onClick={handleMarkAllRead} 
                              style={{ background: 'none', border: 'none', color: 'var(--primary-color)', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}
                            >
                              Tout marquer lu
                            </button>
                          )}
                          {notifications.length > 0 && (
                            <button onClick={handleDeleteAllNotifs} style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }} title="Tout supprimer">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      {notifications.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>Aucune notification</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {notifications.map((n, index) => (
                            <React.Fragment key={n.id}>
                              <div onClick={() => handleNotifClick(n)} style={{ padding: '0.8rem', backgroundColor: n.read ? 'transparent' : 'rgba(168, 159, 207, 0.1)', borderRadius: 'var(--border-radius-sm)', cursor: 'pointer', borderLeft: n.read ? 'none' : '3px solid var(--primary-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                                <div style={{ fontSize: '0.9rem', flex: 1 }}>{n.message}</div>
                                <button onClick={(e) => handleDeleteNotif(e, n.id)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '0.2rem' }} title="Supprimer">
                                  <X size={14} />
                                </button>
                              </div>
                              {index < notifications.length - 1 && (
                                <div style={{ height: '1px', backgroundColor: 'var(--border-color)', opacity: 0.5, margin: '0 0.5rem' }} />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {/* Bouton d'installation PWA */}
          {(deferredPrompt || isIosInstall) && (
            <button 
              className="btn-icon" 
              onClick={() => {
                if (deferredPrompt) {
                  handleInstallClick();
                } else if (isIosInstall) {
                  setShowIosModal(true);
                }
              }} 
              title="Installer l'application"
              style={{ color: 'var(--primary-color)' }}
            >
              <Download size={24} />
            </button>
          )}
          <Link to="/changelog" className="btn-icon" title="Nouveautés">
            <FileText size={20} />
          </Link>
          <button className="btn-icon" onClick={toggleTheme} title="Changer le thème (Clair/Sombre)">
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          {!currentProfile && (
            <>
              <button className="btn-icon" onClick={() => setShowAccountSettings(true)} title="Paramètres du compte">
                <SettingsIcon size={24} />
              </button>
              <button 
                className="btn-icon" 
                onClick={async () => {
                  try {
                    await logout();
                  } catch (e) {
                    console.error("Erreur déconnexion:", e);
                  }
                }} 
                title="Déconnexion"
              >
                <LogOut size={24} color="#ff6b6b" />
              </button>
              {showAccountSettings && (
                <AccountSettings onClose={() => setShowAccountSettings(false)} />
              )}
            </>
          )}
        </div>
      </div>

      {currentProfile && (
        <style>{`
          .mobile-only-header-stats {
            display: none;
          }
          @media (max-width: 768px) {
            .mobile-only-header-stats {
              display: flex;
            }
          }
        `}</style>
      )}

      {currentProfile && (
        <div className="mobile-only-header-stats" style={{ justifyContent: 'space-between', alignItems: 'center', width: '100%', backgroundColor: 'var(--bg-color)', padding: '0.6rem 1rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', gap: '1rem', flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none' }}>
          <div className="header-currency" style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 'bold' }} title="Pièces">
              🪙 <span>{currentProfile.coins || 0}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 'bold' }} title="Diamants">
              💎 <span>{currentProfile.diamonds || 0}</span>
            </div>
          </div>

          <div className="header-level" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: '0.8rem' }} title={`Niveau ${level} : ${xpInCurrentLevel} / ${xpForNextLevel} XP`}>
            <span style={{ fontWeight: 'bold', color: 'var(--secondary-color)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal' }}>{grade} (Niv. {level})</span> 
              <span style={{ color: 'var(--text-primary)' }}>{totalXp} XP</span>
            </span>
            <div style={{ width: '100px', height: '6px', backgroundColor: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden', marginTop: '4px', position: 'relative' }} title={`Niveau ${level} : ${xpInCurrentLevel} / ${xpForNextLevel} XP\nTotal cumulé : ${totalXp} XP`}>
              <div style={{ width: `${progress}%`, height: '100%', backgroundColor: 'var(--secondary-color)', transition: 'width 0.3s ease' }}></div>
            </div>
          </div>
        </div>
      )}
      
      {selectedFeedbackReply && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(5px)' }} onClick={() => setSelectedFeedbackReply(null)}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', animation: 'scaleIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            
            {/* Header */}
            <div style={{ padding: '1.2rem', backgroundColor: 'var(--bg-color)', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '1.1rem' }}>
                <ShieldAlert size={20} color="var(--primary-color)" /> Réponse de l'équipe
              </h3>
              <button className="btn-icon" onClick={() => setSelectedFeedbackReply(null)} title="Fermer" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem', maxHeight: '60vh', overflowY: 'auto', backgroundColor: 'var(--bg-card)' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}>
                  <img loading="lazy" src="/logos/logo-officiel-transparent.png" alt="Admin" style={{ width: '24px', height: '24px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                </div>
                
                <div style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.3rem', fontWeight: 'bold' }}>Administrateur Masse-terclass</span>
                  <div style={{ backgroundColor: 'rgba(168, 159, 207, 0.1)', padding: '1rem 1.2rem', borderRadius: 'var(--border-radius)', borderTopLeftRadius: '0', border: '1px solid var(--border-color)' }}>
                    <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', color: 'var(--text-primary)', margin: 0, lineHeight: 1.6, fontSize: '0.95rem' }}>
                      {selectedFeedbackReply}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', backgroundColor: 'var(--bg-color)' }}>
              <button className="btn btn-primary" onClick={() => setSelectedFeedbackReply(null)} style={{ padding: '0.5rem 1.5rem' }}>Compris !</button>
            </div>
            
          </div>
        </div>
      )}

      {showIosModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(5px)' }} onClick={() => setShowIosModal(false)}>
          <div className="card" style={{ width: '100%', maxWidth: '400px', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Installer sur iPhone</h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
              Pour installer Masse-terclass sur ton écran d'accueil, suis ces étapes :
            </p>
            <ol style={{ textAlign: 'left', color: 'var(--text-secondary)', margin: 0, paddingLeft: '1.5rem', lineHeight: '1.5' }}>
              <li>Appuie sur le bouton <b>Partager</b> <Share size={16} style={{ verticalAlign: 'middle' }}/> (en bas de l'écran sur Safari, ou en haut à droite sur Chrome).</li>
              <li>Fais défiler la liste des options vers le bas (ou appuie sur "En voir plus").</li>
              <li>Choisis <b>"Sur l'écran d'accueil"</b> <span style={{fontSize: '1.2rem', verticalAlign: 'middle'}}>âž•</span></li>
            </ol>
            <button className="btn btn-primary" onClick={() => setShowIosModal(false)} style={{ marginTop: '1rem', width: '100%' }}>J'ai compris</button>
          </div>
        </div>
      )}

      {showAccountSettings && <AccountSettings onClose={() => setShowAccountSettings(false)} />}
    </header>
  );
};

function App() {
  const { currentUser } = useAuth();
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('fitTrackTheme') || 'dark';
  });
  const [currentProfile, setCurrentProfile] = useState(() => {
    try {
      const cached = localStorage.getItem('fitTrackCurrentProfile');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIosInstall, setIsIosInstall] = useState(false);
  const [previewThemeId, setPreviewThemeId] = useState(null);

  useEffect(() => {
    const handlePreview = (e) => setPreviewThemeId(e.detail);
    const handlePreviewEnd = () => setPreviewThemeId(null);
    window.addEventListener('themePreview', handlePreview);
    window.addEventListener('themePreviewEnd', handlePreviewEnd);
    return () => {
      window.removeEventListener('themePreview', handlePreview);
      window.removeEventListener('themePreviewEnd', handlePreviewEnd);
    };
  }, []);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);

  useEffect(() => {
    if (currentUser) {
      const hasOnboardedFlag = localStorage.getItem(`fitTrackOnboarded_${currentUser.uid}`);
      if (hasOnboardedFlag === 'true' || currentProfile) {
        setCheckingOnboarding(false);
        return;
      }

      setCheckingOnboarding(true);
      const fallbackTimer = setTimeout(() => {
        setCheckingOnboarding(false);
      }, 2500);

      getDoc(doc(firestoreDb, "users", currentUser.uid)).then(async (snap) => {
        clearTimeout(fallbackTimer);
        if (!snap.exists()) {
          try {
            const { getProfiles } = await import('./db');
            const profiles = await getProfiles(currentUser.uid);
            if (profiles.length > 0) {
              // Old user who already has profiles, create the user doc silently
              const { saveUserOnboarding } = await import('./db');
              await saveUserOnboarding(currentUser.uid, {
                firstName: currentUser.displayName?.split(' ')[0] || '',
                lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
                migrated: true,
                experience: 'Non défini',
                frequency: 'Non défini',
                goal: 'Non défini'
              });
              localStorage.setItem(`fitTrackOnboarded_${currentUser.uid}`, 'true');
              setNeedsOnboarding(false);
            } else {
              setNeedsOnboarding(true);
            }
          } catch (e) {
            console.error(e);
            setNeedsOnboarding(true);
          }
        } else {
          localStorage.setItem(`fitTrackOnboarded_${currentUser.uid}`, 'true');
        }
        setCheckingOnboarding(false);
      }).catch(err => {
        clearTimeout(fallbackTimer);
        console.error(err);
        setCheckingOnboarding(false);
      });

      return () => clearTimeout(fallbackTimer);
    }
  }, [currentUser, currentProfile]);

  // Force clear any stuck inline styles on root from previous HMR glitches
  useEffect(() => {
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.style.maxWidth = '';
      rootEl.style.padding = '';
      rootEl.style.margin = '';
      rootEl.classList.remove('full-width');
    }
    document.body.classList.remove('auth-bg');
  }, [currentProfile]);

  useEffect(() => {
    // Check if it's iOS and not already installed
    const checkIos = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };
    const isInStandaloneMode = () => ('standalone' in window.navigator) && (window.navigator.standalone);

    if (checkIos() && !isInStandaloneMode()) {
      setIsIosInstall(true);
    }

    // Check if the event fired before React mounted
    if (window.deferredPWAInstallPrompt) {
      setDeferredPrompt(window.deferredPWAInstallPrompt);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPWAInstallPrompt = e;
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    const activeThemeId = previewThemeId || currentProfile?.equipped?.theme;
    
    if (activeThemeId) {
      const customTheme = shopItems.find(i => i.id === activeThemeId);
      if (customTheme) {
        if (customTheme.themeMode) {
          root.setAttribute('data-theme', customTheme.themeMode);
          document.body.setAttribute('data-theme', customTheme.themeMode);
        } else {
          root.setAttribute('data-theme', theme);
          document.body.setAttribute('data-theme', theme);
        }
        if (customTheme.variables) {
          Object.entries(customTheme.variables).forEach(([key, value]) => {
            root.style.setProperty(key, value);
            document.body.style.setProperty(key, value);
          });
        }
      }
    } else {
      root.setAttribute('data-theme', theme);
      document.body.setAttribute('data-theme', theme);
      
      // Forcefully clear any stuck properties on body too just in case
      document.body.style.removeProperty('background-color');
      ['--primary-color', '--primary-color-hover', '--secondary-color', '--secondary-color-hover', '--accent-color', '--bg-color', '--bg-card', '--text-primary', '--text-secondary'].forEach(prop => {
        root.style.removeProperty(prop);
        document.body.style.removeProperty(prop);
      });
    }
  }, [theme, previewThemeId, currentProfile?.equipped?.theme]);

  // Save theme to localStorage
  useEffect(() => {
    localStorage.setItem('fitTrackTheme', theme);
  }, [theme]);

  // Auto-login from localStorage
  useEffect(() => {
    if (!currentUser) {
      setIsInitializing(false);
      return;
    }
    const savedId = localStorage.getItem('fitTrackProfileId');
    if (savedId) {
      const timeout = setTimeout(() => {
        setIsInitializing(false);
      }, 2500);

      db.getProfile(savedId).then(p => {
        clearTimeout(timeout);
        if (p && p.userIds && p.userIds.includes(currentUser.uid)) {
          setCurrentProfile(p);
          localStorage.setItem('fitTrackCurrentProfile', JSON.stringify(p));
        } else if (!p && !navigator.onLine) {
          // Keep currentProfile if offline
        } else if (p && (!p.userIds || !p.userIds.includes(currentUser.uid))) {
          localStorage.removeItem('fitTrackProfileId');
          localStorage.removeItem('fitTrackCurrentProfile');
        }
        setIsInitializing(false);
      }).catch(err => {
        clearTimeout(timeout);
        console.warn("Erreur auto-login getProfile (possiblement hors-ligne):", err);
        setIsInitializing(false);
      });
      return () => clearTimeout(timeout);
    } else {
      setIsInitializing(false);
    }
  }, [currentUser]);

  // Sync profile data (XP, level) in real time and keep localStorage in sync
  useEffect(() => {
    if (currentProfile?.id) {
      localStorage.setItem('fitTrackCurrentProfile', JSON.stringify(currentProfile));
      const unsubscribe = db.listenToProfile(currentProfile.id, (updatedProfile) => {
        if (updatedProfile) {
          setCurrentProfile(updatedProfile);
          localStorage.setItem('fitTrackCurrentProfile', JSON.stringify(updatedProfile));
        }
      });
      return () => unsubscribe();
    }
  }, [currentProfile?.id]);

  // Manage Online Status
  useEffect(() => {
    if (!currentProfile?.id) return;
    
    // Set online on mount
    db.setProfileOnlineStatus(currentProfile.id, true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        db.setProfileOnlineStatus(currentProfile.id, true);
      } else {
        db.setProfileOnlineStatus(currentProfile.id, false);
      }
    };

    const handleBeforeUnload = () => {
      db.setProfileOnlineStatus(currentProfile.id, false);
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Ping every 5 minutes
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        db.setProfileOnlineStatus(currentProfile.id, true);
      }
    }, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(interval);
      db.setProfileOnlineStatus(currentProfile.id, false);
    };
  }, [currentProfile?.id]);

  const toggleTheme = () => {
    if (currentProfile?.equipped?.theme) {
      toast.info("Thème personnalisé actif. Déséquipez-le pour modifier le mode manuel.");
      return;
    }
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const handleLogout = () => {
    setCurrentProfile(null);
    localStorage.removeItem('fitTrackProfileId');
    localStorage.removeItem('fitTrackCurrentProfile');
  };

  if (isInitializing) {
    return <Loader fullScreen={true} message="Lancement de Masse-terclass..." />;
  }

  const isAuthActionRoute = window.location.pathname === '/auth-action';

  if (currentUser && !currentUser.emailVerified && !isAuthActionRoute) {
    return <VerificationScreen />;
  }

  if (!currentUser) {
    return (
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth-action" element={<AuthAction />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal" element={<LegalMentions />} />
            <Route path="/cgu" element={<CGU />} />
            <Route path="/privacy" element={<PolicyPage />} />
            <Route path="/test" element={<TestBossV2 />} />
            <Route path="/test2" element={<TestBossV2 />} />
            <Route path="/test3" element={<TestBossV3 />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <GlobalFeedback />
        {currentProfile && <FloatingFeedbackButton currentProfile={currentProfile} />}
        <Header theme={theme} toggleTheme={toggleTheme} currentProfile={currentProfile} onLogout={handleLogout} deferredPrompt={deferredPrompt} handleInstallClick={handleInstallClick} isIosInstall={isIosInstall} previewThemeId={previewThemeId} />
        
        {isAuthActionRoute ? (
          <main>
            <Routes>
              <Route path="/auth-action" element={<AuthAction />} />
            </Routes>
          </main>
        ) : !currentProfile ? (
          <main>
            {checkingOnboarding ? (
              <Loader fullScreen={true} message="Chargement..." />
            ) : needsOnboarding ? (
              <OnboardingScreen onComplete={() => {
                if (currentUser) {
                  localStorage.setItem(`fitTrackOnboarded_${currentUser.uid}`, 'true');
                }
                setNeedsOnboarding(false);
              }} />
            ) : (
              <ProfileSelection onLogin={setCurrentProfile} />
            )}
          </main>
        ) : (
          <>
            <Navigation currentProfile={currentProfile} />
            <main>
              <Routes>
                  <Route path="/" element={<Dashboard currentProfile={currentProfile} theme={theme} />} />
                  <Route path="/changelog" element={<Changelog />} />
                  <Route path="/test" element={<TestBossV2 currentProfile={currentProfile} />} />
                  <Route path="/test2" element={<TestBossV2 currentProfile={currentProfile} />} />
                  <Route path="/test3" element={<TestBossV3 currentProfile={currentProfile} />} />
                  <Route path="/boss" element={<Navigate to="/social?tab=boss" replace />} />
                  <Route path="/world-boss" element={<Navigate to="/social?tab=boss" replace />} />
                  <Route path="/mascot-test" element={<MascotTest />} />
                <Route path="/mascot-showcase" element={<MascotShowcase />} />
                <Route path="/routines" element={<Routines currentProfile={currentProfile} />} />
                <Route path="/history" element={<HistoryPage currentProfile={currentProfile} />} />
                <Route path="/body" element={<BodyTracker currentProfile={currentProfile} />} />
                <Route path="/goals" element={<Goals currentProfile={currentProfile} />} />
                <Route path="/library" element={<ExerciseLibrary currentProfile={currentProfile} />} />
                <Route path="/leaderboard" element={<Leaderboard currentProfile={currentProfile} />} />
                <Route path="/social" element={<Social currentProfile={currentProfile} />} />
                <Route path="/badges" element={<Badges currentProfile={currentProfile} />} />
                <Route path="/messages" element={<Messages currentProfile={currentProfile} />} />
                <Route path="/forum" element={<Forum currentProfile={currentProfile} />} />
                <Route path="/shop" element={<Shop currentProfile={currentProfile} updateProfileLocal={setCurrentProfile} />} />
                <Route path="/profile" element={<PublicProfile currentProfile={currentProfile} />} />
                <Route path="/profile/:id" element={<PublicProfile currentProfile={currentProfile} />} />
                <Route path="/settings" element={<Settings currentProfile={currentProfile} onUpdate={setCurrentProfile} onLogout={handleLogout} />} />
                <Route path="/legal" element={<LegalMentions />} />
                <Route path="/cgu" element={<CGU />} />
                <Route path="/privacy" element={<PolicyPage />} />
                <Route path="/admin" element={<AdminDashboard currentProfile={currentProfile} />} />
                <Route path="/auth-action" element={<AuthAction />} />
                <Route path="/auth" element={<Navigate to="/" />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </>
        )}
      </Router>
    </HelmetProvider>
  );
}

export default App;



